import PropTypes from 'prop-types';
import { Form } from 'react-final-form';
import styled from '@emotion/styled/macro';
import MarkdownField from './MarkdownField';
import { Button, CancelLinkButton } from '../../form';
import { leading, settings } from '../../styles/theme';
import { isRequired } from '../validation';
import LoadingSpinner from 'components/common/LoadingSpinner';
import { useState, useRef, useCallback } from 'react';
import { Styles } from '../../../../toolkit/components/';

const CommentFormContainer = styled.div`
  background: #f8f8f8;
  padding: 22px;
  width: ${(props) => (props.width ? props.width : '350px')};
  border: 1px solid #d4d4d4;
  border-left: ${(props) => (props.borderLeft ? `3px solid ${Styles.colors.orange.primary}` : `1px solid ${Styles.colors.grey.secondaryDark};`)};
  border-radius: 3px;
  textarea {
    min-height: 40px;
    /* The global forms.css adds a teal focus ring (box-shadow: 0 0 0 3px rgba(0,166,194,.1)) to
       every textarea. Inside the bordered tab-panel card that ring rendered as a distinct inner
       "box", giving the comment form a box-in-a-box look classic never had (its toolkit textarea
       had no such ring). Suppress it so the panel card is the single boundary. (#21234) */
    box-shadow: none;
    border: 0;
    &:focus {
      box-shadow: none;
      outline: none;
    }
  }

  .react-tabs__tab-panel {
    min-height: 40px;
  }

  /* eng-maintenance#21234: the restored Write|Guide tab panel renders a bordered card, but the
     react-mentions multiLine input is position:absolute (min-height 100px) so its wrappers collapse
     to one line — the input then overflowed the card, painting a second offset box with its focus
     ring. Give the Write panel's mention wrapper divs the input's height so the panel grows to
     contain it. Scoped to exclude the Guide panel (which sizes to its own content). */
  .react-tabs__tab-panel--selected:not(.react-tabs__tab-panel-guide) > div,
  .react-tabs__tab-panel--selected:not(.react-tabs__tab-panel-guide) > div > div {
    min-height: 100px;
  }

  @media screen and (max-width: ${settings.mobile.maxWidth}) {
    width: auto;
  }
`;

const CommentFormActions = styled.div`
  text-align: right;
  position: relative;
  width: 100%;
  vertical-align: top;
  display: inline-block;
  padding: 10px 0;
  margin-top: ${leading.half};
  border-radius: 0 0 5px 5px;
  box-sizing: border-box;
`;

export default function CommentForm({ body, onSubmit, onCancel, width, users, onTextChange, borderLeft}) {
  const [savingInProgress, setSavingInProgress] = useState(false);
  
  // Track mentioned user IDs as they're selected from dropdown
  const mentionMapRef = useRef(new Map());

  const handleMentionAdd = useCallback((id, display) => {
    mentionMapRef.current.set(display, Number(id));
  }, []);

  const handleFormSubmission = async (data) => {
    setSavingInProgress(true);
    
    // Extract mentioned user IDs from tracked mentions
    const mentionPattern = /\*\*([^*]+)\*\*/g;
    const currentMentions = [];
    let match;
    while ((match = mentionPattern.exec(data.body)) !== null) {
      currentMentions.push(match[1].trim());
    }

    const mentionedUserIds = currentMentions
      .map((display) => mentionMapRef.current.get(display))
      .filter((id) => id !== undefined);

    onSubmit({ ...data, mentionedUserIds });
    mentionMapRef.current.clear();
  };

  return (
    <CommentFormContainer width={width} borderLeft={borderLeft}>
      <Form
        initialValues={{ body }}
        onSubmit={handleFormSubmission}
        validate={(values) => isRequired(values.body)}
        render={({ handleSubmit, invalid }) => (
          <form onChange={(event) => onTextChange(event.target.value)}>
            <MarkdownField
              disabled={savingInProgress}
              name="body"
              label="Leave a comment"
              rows={4}
              users={users}
              onMentionAdd={handleMentionAdd}
            />
            <CommentFormActions>
              <CancelLinkButton
                size="small"
                onClick={() => {
                  onCancel();
                }}
              />
              <Button
                type="submit"
                size="small"
                variant="secondary"
                disabled={invalid || savingInProgress}
                onClick={handleSubmit}
              >
                {savingInProgress ? <>Saving<LoadingSpinner /></>: "Comment"}
              </Button>
            </CommentFormActions>
          </form>
        )}
      />
    </CommentFormContainer>
  );
}

const userShape = {
  id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  display: PropTypes.string.isRequired,
  avatar: PropTypes.string.isRequired,
};

CommentForm.defaultProps = {
  body: '',
  width: null,
  users: [],
  onTextChange: () => {},
};

CommentForm.propTypes = {
  body: PropTypes.string,
  users: PropTypes.arrayOf(PropTypes.shape(userShape)),
  onSubmit: PropTypes.func.isRequired,
  onTextChange: PropTypes.func,
  onCancel: PropTypes.func.isRequired,
  width: PropTypes.string,
};
